import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  useDisclosure,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Text,
  Box,
  Divider,
  VStack,
  HStack,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Select,
  Progress,
  FormLabel,
} from '@chakra-ui/react';

import { MdFileCopy, MdFolder } from 'react-icons/md';
import { AttachmentIcon, CloseIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';

import { createFileSubmissionSubQuest } from 'store/modules/subquest/subquest-action';
import { ReducerStatus } from 'utils/constants';
import ResultModal from 'components/common/modals/ResultModal';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import SubQuestForm from 'components/subQuest/SubQuestForm';
import NumberInput from 'components/common/forms/NumberInput';
import useFileInput from 'hooks/useFileInput';
import { arrayOfString } from 'utils/jsonParser';
import { subquestActions } from 'store/modules/subquest/subquest-slice';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import imageCompressor from 'quill-image-compress';

import {
  useQuillConfig,
  calculateContentSize,
} from 'utils/react-quill/quillConfig';
import RichTextSizeIndicator from 'utils/react-quill/RichTextSizeIndicator';
import FormikFormControl from 'components/common/forms/FormikFormControl';

ReactQuill.Quill.register('modules/imageCompress', imageCompressor);

function CreateFileSubmissionMd({ quest }) {
  const { modules, formats, style } = useQuillConfig();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();

  const bottomRef = useRef(null);

  const [triggerSubQuestForm, setTriggerSubQuestForm] = useState(0);
  const [isSubQuestFormValid, setIsSubQuestFormValid] = useState(false);
  const [isFormSubmit, setIsFormSubmit] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [subQuestFormInputs, setSubQuestFormInputs] = useState({
    name: '',
    objective: '',
    code: '',
    isPublished: '',
    tags: [],
    groups: [],
  });

  const [formInputs, setFormInputs] = useState({
    scoreType: 'Actual Score',
    totalScore: 100,
    passingScore: 60,
    requirements: '',
    answerSettings: 'One Time Submission',
  });
  const [contentSize, setContentSize] = useState(0);

  const createFileSubmission = useSelector(
    (state) => state.subquest.createFileSubmission
  );

  const uploadProgress = useSelector((state) => state.subquest.uploadProgress);

  const {
    attachedFiles,
    fileError,
    fileInputRef,
    setFileError,
    handleAttachFile,
    removeAttachedFile,
    clearAllFiles,
    handleFileInputChange,
  } = useFileInput({
    maxFileSizeMb: 30,
    fileType: 'application/x-zip-compressed',
    multiple: true,
  });

  const formikForm = useRef();
  const quillRef = useRef(null);

  const formValidation = Yup.object({
    scoreType: Yup.string().required('Required'),
    totalScore: Yup.number()
      .min(1, 'Total score must be at least 1')
      .required('Required'),
    passingScore: Yup.number()
      .min(0, 'Passing score cannot be negative')
      .max(Yup.ref('totalScore'), 'Passing score cannot exceed total score')
      .required('Required'),
    requirements: Yup.string()
      .test('not-empty', 'Requirements instructions are required', (value) => {
        if (!value) return false;
        const textContent = value.replace(/<[^>]*>/g, '').trim();
        return textContent.length > 0;
      })
      .test('file-size', 'Content size exceeds 5MB limit', (value) => {
        if (!value) return true;
        const calculatedSize = calculateContentSize(value);
        return calculatedSize <= 5;
      })
      .required('Requirements instructions are required'),
    answerSettings: Yup.string().required('Required'),
  });

  useEffect(() => {
    if (attachedFiles.length > 0) setFileError('');
  }, [attachedFiles, setFileError]);

  useEffect(() => {
    if (quest?.ownerGroups) {
      setSubQuestFormInputs((prev) => ({
        ...prev,
        groups: arrayOfString(quest.ownerGroups),
      }));
    }
  }, [quest?.ownerGroups]);

  useEffect(() => {
    if (isFormSubmit && isSubQuestFormValid) {
      formikForm?.current?.submitForm();
      setIsFormSubmit(false);
    }
  }, [isFormSubmit, isSubQuestFormValid, setIsFormSubmit]);

  const handleTriggerSubmit = () => {
    formikForm.current?.setFieldTouched('requirements', true, false);
    formikForm.current?.validateField('requirements');

    setTriggerSubQuestForm((trigger) => trigger + 1);
    setIsFormSubmit(true);
  };

  const handleSubmitForm = (values) => {
    if (isSubQuestFormValid === false) return;

    setPendingFormValues(values);
    setShowConfirmation(true);
  };

  const handleConfirmSubmission = () => {
    if (!pendingFormValues) return;

    setFormInputs({ ...pendingFormValues });

    const formData = new FormData();

    Object.entries(subQuestFormInputs).forEach(([key, value]) => {
      if (key !== 'tags' && key !== 'groups') {
        formData.append(key, value);
      }
    });

    if (subQuestFormInputs.tags.length > 0)
      subQuestFormInputs.tags.forEach((tag) => formData.append('Tags', tag));
    if (subQuestFormInputs.groups.length > 0)
      subQuestFormInputs.groups.forEach((group) =>
        formData.append('OwnerGroups', group)
      );

    Object.entries(pendingFormValues).forEach(([key, value]) => {
      formData.append(key, value);
    });

    if (attachedFiles && attachedFiles.length > 0) {
      attachedFiles.forEach((fileItem) => {
        if (fileItem.file) {
          let processedName = fileItem.file.name.trim();
          if (processedName.toLowerCase().endsWith('.zip')) {
            processedName = processedName.slice(0, -4);
          }

          formData.append('ZipFiles', fileItem.file, processedName);
        }
      });
    }

    dispatch(
      createFileSubmissionSubQuest({
        id: quest.id,
        formData,
      })
    );

    setShowConfirmation(false);
    setPendingFormValues(null);

    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingFormValues(null);
  };

  const handleConfirmationResult = () => {
    setShowConfirmation(false);
  };

  const handleResult = () => {
    if (createFileSubmission.status === ReducerStatus.SUCCEEDED) {
      setFormInputs({
        scoreType: 'Actual Score',
        totalScore: 100,
        passingScore: 60,
        requirements: '',
        answerSettings: 'One Time Submission',
      });

      setSubQuestFormInputs({
        name: '',
        objective: '',
        code: '',
        isPublished: '',
        tags: [],
        groups: [],
      });

      setTriggerSubQuestForm(-1);
      clearAllFiles();
      onClose();
    }
    dispatch(
      subquestActions.setCreateFileSubmission({
        status: ReducerStatus.IDLE,
        error: null,
      })
    );
  };

  const handleToggleModal = () => {
    if (createFileSubmission.status === ReducerStatus.LOADING) return;
    onClose();
  };

  const getConfirmationDescription = () => {
    if (!pendingFormValues) return '';

    const hasTemplates = attachedFiles.length > 0;
    const scoreInfo =
      pendingFormValues.scoreType === 'Percentage'
        ? `${pendingFormValues.passingScore}% passing requirement rate`
        : `${pendingFormValues.passingScore}/${pendingFormValues.totalScore} points to pass`;

    const submissionType = pendingFormValues.answerSettings;

    return (
      <>
        <Text mb={3}>
          Are you sure you want to create this file submission sub-quest?
        </Text>
        <VStack align="start" spacing={1}>
          <Text>
            Sub-quest Name:{' '}
            <Text as="span" fontWeight="bold">
              {subQuestFormInputs.name}
            </Text>
          </Text>
          <Text>
            Score Requirements:{' '}
            <Text as="span" fontWeight="bold">
              {scoreInfo}
            </Text>
          </Text>
          <Text>
            Submission Type:{' '}
            <Text as="span" fontWeight="bold">
              {submissionType}
            </Text>
          </Text>
          {hasTemplates && (
            <Box>
              <Text mb={1}>
                Project Templates:{' '}
                <Text as="span" fontWeight="bold">
                  {attachedFiles.length} file(s)
                </Text>
              </Text>
              <VStack align="start" spacing={1} pl={4}>
                {attachedFiles.map((fileItem) => (
                  <Text key={fileItem.id} fontSize="sm" color="gray.600">
                    • {fileItem.name}
                  </Text>
                ))}
              </VStack>
            </Box>
          )}
        </VStack>
      </>
    );
  };

  return (
    <Box>
      <ConfirmationActionModal
        isVisible={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmission}
        onSubmitResult={handleConfirmationResult}
        status={createFileSubmission.status}
        successMessage="The file submission sub-quest has been created successfully."
        error={createFileSubmission.error}
      >
        {getConfirmationDescription()}
      </ConfirmationActionModal>

      {createFileSubmission.status === ReducerStatus.SUCCEEDED ||
      createFileSubmission.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={isOpen}
          onSubmit={handleResult}
          onClose={handleToggleModal}
          isError={createFileSubmission.status === ReducerStatus.FAILED}
          title={
            createFileSubmission.status === ReducerStatus.SUCCEEDED
              ? 'File Submission Sub-Quest Created'
              : null
          }
          message={
            createFileSubmission.status === ReducerStatus.SUCCEEDED
              ? 'The file submission sub-quest has been created successfully.'
              : createFileSubmission.error?.data?.title || 'Server Error'
          }
          errorObject={createFileSubmission.error?.data?.errors}
        />
      ) : (
        <>
          <Button
            onClick={onOpen}
            leftIcon={<MdFileCopy />}
            w="100%"
            colorScheme="red"
            size="lg"
          >
            File Submission
          </Button>
          <Modal
            size="3xl"
            closeOnOverlayClick={
              createFileSubmission.status !== ReducerStatus.LOADING
            }
            isOpen={isOpen}
            onClose={handleToggleModal}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Create File Submission Sub-Quest</ModalHeader>
              <ModalCloseButton
                disabled={createFileSubmission.status === ReducerStatus.LOADING}
              />
              <ModalBody my={3}>
                <SubQuestForm
                  questName={quest.name}
                  groups={quest.ownerGroups}
                  subQuestFormInputs={subQuestFormInputs}
                  triggerSubQuestForm={triggerSubQuestForm}
                  setSubQuestFormInputs={setSubQuestFormInputs}
                  setIsSubQuestFormValid={setIsSubQuestFormValid}
                />
              </ModalBody>
              <Formik
                enableReinitialize
                innerRef={formikForm}
                initialValues={formInputs}
                onSubmit={(values) => {
                  handleSubmitForm(values);
                }}
                validationSchema={formValidation}
              >
                {({ values, setFieldValue }) => (
                  <Box>
                    <Form>
                      <ModalBody>
                        <Divider mb={4} />

                        <VStack spacing={6} align="stretch">
                          <Text fontSize="lg" fontWeight="bold">
                            File Submission Settings
                          </Text>

                          <Box>
                            <VStack spacing={3} align="stretch">
                              <HStack spacing={4} align="flex-start">
                                <Field name="scoreType">
                                  {({ field, form }) => (
                                    <FormikFormControl
                                      label="Score Type"
                                      helperText="Compute scores as a percentage or as an actual score."
                                      errors={form.errors.scoreType}
                                      touched={form.touched.scoreType}
                                      flex={
                                        values.scoreType === 'Percentage'
                                          ? '3'
                                          : '2'
                                      }
                                    >
                                      <Select
                                        {...field}
                                        onChange={({ target: { value } }) => {
                                          setFieldValue('scoreType', value);
                                          if (value === 'Percentage') {
                                            setFieldValue('totalScore', 100);
                                          }
                                        }}
                                      >
                                        <option value="Actual Score">
                                          Actual Score
                                        </option>
                                        <option value="Percentage">
                                          Percentage
                                        </option>
                                      </Select>
                                    </FormikFormControl>
                                  )}
                                </Field>

                                {values.scoreType !== 'Percentage' && (
                                  <Field name="totalScore">
                                    {({ field, form }) => (
                                      <NumberInput
                                        label="Total Score"
                                        helperText="Maximum score for this sub-quest."
                                        errors={form.errors.totalScore}
                                        touched={form.touched.totalScore}
                                        flex="1"
                                        field={field}
                                        min={1}
                                        defaultValue={100}
                                        onChange={(value) =>
                                          setFieldValue(
                                            'totalScore',
                                            parseInt(value, 10) || 0
                                          )
                                        }
                                      />
                                    )}
                                  </Field>
                                )}

                                <Field name="passingScore">
                                  {({ field, form }) => (
                                    <NumberInput
                                      label={
                                        values.scoreType === 'Percentage'
                                          ? 'Passing Percentage'
                                          : 'Passing Score'
                                      }
                                      helperText={
                                        values.scoreType === 'Percentage'
                                          ? 'Minimum percentage required to pass (0-100%)'
                                          : 'Minimum score required to pass.'
                                      }
                                      errors={form.errors.passingScore}
                                      touched={form.touched.passingScore}
                                      flex="1"
                                      field={field}
                                      min={0}
                                      max={values.totalScore}
                                      defaultValue={60}
                                      onChange={(value) =>
                                        setFieldValue(
                                          'passingScore',
                                          parseInt(value, 10) || 0
                                        )
                                      }
                                    />
                                  )}
                                </Field>
                              </HStack>
                            </VStack>
                          </Box>

                          <Field name="requirements">
                            {({ field, form }) => (
                              <FormControl
                                isInvalid={
                                  form.errors.requirements &&
                                  form.touched.requirements
                                }
                              >
                                <FormLabel>Requirements</FormLabel>
                                <Box position="relative" sx={style}>
                                  <ReactQuill
                                    ref={quillRef}
                                    value={field.value}
                                    onChange={(content) => {
                                      const newContentSize =
                                        calculateContentSize(content);
                                      setContentSize(newContentSize);
                                      form.setFieldValue(field.name, content);
                                    }}
                                    onBlur={() => {
                                      form.setFieldTouched(field.name, true);
                                    }}
                                    placeholder="Describe what trainees need to submit..."
                                    style={{
                                      border:
                                        form.errors.requirements &&
                                        form.touched.requirements
                                          ? '2px solid #e53e3e'
                                          : '1px solid #e2e8f0',
                                      borderRadius: '6px',
                                    }}
                                    theme="snow"
                                    modules={modules}
                                    formats={formats}
                                  />
                                  <RichTextSizeIndicator size={contentSize} />
                                </Box>
                                <FormErrorMessage>
                                  {form.errors.requirements}
                                </FormErrorMessage>
                                <FormHelperText>
                                  Provide detailed submission requirements
                                  including file formats, naming conventions,
                                  deliverables, and any specific instructions
                                  trainees need to follow.
                                </FormHelperText>
                              </FormControl>
                            )}
                          </Field>

                          <Field name="answerSettings">
                            {({ field, form }) => (
                              <FormControl
                                isInvalid={
                                  form.errors.answerSettings &&
                                  form.touched.answerSettings
                                }
                              >
                                <FormLabel>Submission Type</FormLabel>
                                <Select {...field}>
                                  <option value="One Time Submission">
                                    One Time Submission
                                  </option>
                                  <option value="Multiple Submission">
                                    Multiple Submission
                                  </option>
                                  <option value="Overwrite Existing Session">
                                    Overwrite Existing Submission
                                  </option>
                                </Select>
                                <FormErrorMessage>
                                  {form.errors.answerSettings}
                                </FormErrorMessage>
                                <FormHelperText>
                                  Control submission behavior: One Time (single
                                  submission only), Multiple (unlimited
                                  attempts), or Overwrite (replace previous
                                  submission).
                                </FormHelperText>
                              </FormControl>
                            )}
                          </Field>

                          <Box>
                            <Text fontSize="md" fontWeight="semibold" mb={3}>
                              Project Templates (Optional)
                            </Text>

                            <FormControl isInvalid={fileError !== ''}>
                              {attachedFiles.length > 0 && (
                                <VStack spacing={2} my={4}>
                                  {attachedFiles.map((fileItem) => (
                                    <Flex
                                      key={fileItem.id}
                                      gap={3}
                                      w="100%"
                                      p={3}
                                      bg="gray.50"
                                      borderRadius="md"
                                      border="1px"
                                      borderColor="gray.200"
                                      align="center"
                                    >
                                      <MdFolder color="teal" size={20} />
                                      <Text flex={1} fontSize="sm">
                                        {fileItem.name}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {(
                                          fileItem.file.size /
                                          (1024 * 1024)
                                        ).toFixed(2)}
                                        MB
                                      </Text>
                                      <Button
                                        type="button"
                                        colorScheme="red"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          removeAttachedFile(fileItem.id)
                                        }
                                      >
                                        <CloseIcon />
                                      </Button>
                                    </Flex>
                                  ))}
                                </VStack>
                              )}

                              <Box
                                as="button"
                                type="button"
                                borderRadius="md"
                                bg="teal"
                                w="100%"
                                mt={5}
                                px={4}
                                h={10}
                                color="white"
                                onClick={handleAttachFile}
                              >
                                <input
                                  type="file"
                                  hidden
                                  accept=".zip"
                                  multiple
                                  ref={fileInputRef}
                                  onChange={handleFileInputChange}
                                />
                                <AttachmentIcon mx={2} />
                                Attach Project Template ZIP Files
                              </Box>
                              {fileError !== '' && (
                                <FormErrorMessage>{fileError}</FormErrorMessage>
                              )}
                              <FormHelperText>
                                Upload ZIP files containing project templates,
                                starter code, or reference materials for
                                trainees. You can select multiple files. Maximum
                                file size per file: 30MB. Supported format: .zip
                                only.
                              </FormHelperText>
                            </FormControl>
                          </Box>

                          {createFileSubmission.status ===
                            ReducerStatus.LOADING && (
                            <Progress
                              hasStripe
                              isAnimated
                              value={uploadProgress}
                            />
                          )}
                        </VStack>
                      </ModalBody>

                      <ModalFooter ref={bottomRef}>
                        <Button
                          variant="outline"
                          colorScheme="blue"
                          mr={3}
                          onClick={handleToggleModal}
                          isLoading={
                            createFileSubmission.status ===
                            ReducerStatus.LOADING
                          }
                        >
                          Close
                        </Button>
                        <Button
                          isLoading={
                            createFileSubmission.status ===
                            ReducerStatus.LOADING
                          }
                          onClick={handleTriggerSubmit}
                          colorScheme="red"
                          px={10}
                        >
                          Submit
                        </Button>
                      </ModalFooter>
                    </Form>
                  </Box>
                )}
              </Formik>
            </ModalContent>
          </Modal>
        </>
      )}
    </Box>
  );
}

CreateFileSubmissionMd.propTypes = {
  quest: PropTypes.object.isRequired,
};

export default CreateFileSubmissionMd;
